import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { sha256 } from '@noble/hashes/sha2.js';
import { randomBytes } from '@noble/hashes/utils.js';

const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID as string;

const REDIRECT_SCHEME = Capacitor.getPlatform() === 'android' ? 'com.nodr.app' : 'IBL.NODR';
export const REDIRECT_URI = `${REDIRECT_SCHEME}://auth/msal`;
const SCOPES = 'openid profile email';

interface MsalResult {
  code: string;
  codeVerifier: string;
  state: string;
  redirectUri: string;
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function generateVerifier(): string {
  return base64url(randomBytes(32));
}

function computeChallenge(verifier: string): string {
  return base64url(sha256(new TextEncoder().encode(verifier)));
}

export async function msalMobileLogin(nonce: string): Promise<MsalResult> {
  const verifier = generateVerifier();
  const challenge = computeChallenge(verifier);
  const state = base64url(randomBytes(16));

  const authUrl =
    `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
    new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      response_mode: 'query',
      state,
      nonce,
    });

  let resolveResult!: (value: MsalResult) => void;
  let rejectResult!: (reason: Error) => void;
  const result = new Promise<MsalResult>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  const listenerHandle = await App.addListener('appUrlOpen', ({ url }) => {
    const [base, query = ''] = url.split('?');
    if (base.toLowerCase() !== REDIRECT_URI.toLowerCase()) return;

    const params = new URLSearchParams(query);
    const code = params.get('code');
    const returnedState = params.get('state');
    const error = params.get('error');

    clearTimeout(timer);
    listenerHandle.remove();
    Browser.close();

    if (error || !code) {
      rejectResult(new Error(error ?? 'Authentication failed'));
    } else if (returnedState !== state) {
      rejectResult(new Error('Authentication state mismatch'));
    } else {
      resolveResult({ code, codeVerifier: verifier, state, redirectUri: REDIRECT_URI });
    }
  });

  const timer = setTimeout(
    () => {
      listenerHandle.remove();
      rejectResult(new Error('Authentication timed out'));
    },
    5 * 60 * 1000,
  );

  await Browser.open({ url: authUrl });

  return result;
}
